import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Cpu, Plus, Printer, Trash2, PenSquare, Wrench, FileDown, Search, X,
  Paperclip, Upload, FileText, Image as ImageIcon, Eye, Download,
  AlertTriangle, Clock, ChevronLeft, ChevronRight, ShieldCheck, ShieldAlert, Package, QrCode, PackageX,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { downloadRegisterPDF, registerBlobUrl, filterBundleForAsset } from "@/lib/registerPdf";
import { fetchBundle, PdfPreviewDialog, GovFooterNote } from "@/components/registers/shared";
import DatePicker from "@/components/registers/DatePicker";
import ComplaintRegisterTab from "@/components/registers/ComplaintRegisterTab";
import AmcRegisterTab from "@/components/registers/AmcRegisterTab";
import ConnectivityRegisterTab from "@/components/registers/ConnectivityRegisterTab";
import ServiceRegisterTab from "@/components/registers/ServiceRegisterTab";
import InactiveStockPanel from "@/components/registers/InactiveStockPanel";
import PrintAllDialog from "@/components/registers/PrintAllDialog";
import { CATEGORIES, INSTITUTION_TYPES, getCategory } from "@/lib/hardwareTaxonomy";
import { QRCodeSVG } from "qrcode.react";

const EMPTY_ITEM = {
  institution_type: "",
  institution_name: "",
  category: "",
  subtype: "",
  brand: "",
  local_body_name: "",
  type_of_instrument: "",
  item_name: "",
  manufacturer_model: "",
  specifications: "",
  date_of_supply: "",
  supplied_by: "",
  amc_from: "",
  amc_to: "",
  service_provider: "",
  amc_no_amount: "",
  initials_of_secretary: "",
  verified_sa: "",
  initial_warranty_from: "",
  initial_warranty_to: "",
  warranty_period: "",
  serial_no: "",
  present_condition: "",
  bill_no_date: "",
  cd_docs: "",
  remarks: "",
  vendor_name: "",
  vendor_contact_person: "",
  vendor_phone: "",
  vendor_email: "",
  vendor_address: "",
  vendor_gst: "",
  assigned_user: "",
  status: "",
};

const EMPTY_MAINT = {
  reporting_date: "",
  complaint_remarks: "",
  service_date: "",
  solved: "",
  service_remarks: "",
  technician_contact: "",
  tracking_number: "",
};

const REMINDER_WINDOW_DAYS = 14;

function parseIsoDate(s) {
  if (!s) return null;
  const d = new Date(String(s).slice(0, 10) + "T00:00:00Z");
  return isNaN(d.getTime()) ? null : d;
}

function daysUntil(dateStr) {
  const d = parseIsoDate(dateStr);
  if (!d) return null;
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86400000);
}

function reminderStatus(dateStr, windowDays = REMINDER_WINDOW_DAYS) {
  const delta = daysUntil(dateStr);
  if (delta === null) return { status: "none", delta: null };
  if (delta < 0) return { status: "expired", delta };
  if (delta <= windowDays) return { status: "expiring_soon", delta };
  return { status: "ok", delta };
}

function reminderLabel(kind, r) {
  if (r.status === "expired") {
    const d = Math.abs(r.delta);
    return `${kind} expired ${d} day${d === 1 ? "" : "s"} ago`;
  }
  if (r.status === "expiring_soon") {
    return `${kind} expires in ${r.delta} day${r.delta === 1 ? "" : "s"}`;
  }
  return null;
}

export default function HardwareRegisterTab() {
  const { user } = useAuth();
  const defaultLocalBody = user?.local_body_name || "";
  const [items, setItems] = useState([]);
  const [complaints, setComplaints] = useState([]);   // unified Complaint Call Logging Register
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);         // null = closed, {} = new, {id,...} = edit
  const [maintOf, setMaintOf] = useState(null);         // hardware item being maintained
  const [maintEditing, setMaintEditing] = useState(null); // maint entry {id,...} or {} for new
  const [attachOf, setAttachOf] = useState(null);       // item whose attachments are open
  const [qrOf, setQrOf] = useState(null);               // item whose QR label is displayed
  const [qrSheetOpen, setQrSheetOpen] = useState(false); // multi-asset QR sheet dialog
  const [currentIdx, setCurrentIdx] = useState(0);      // which record is currently displayed
  const [subTab, setSubTab] = useState("hardware");
  const [printAllOpen, setPrintAllOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");

  // Official-format single-asset register (Annexure-1A + Annexure-1)
  const printSingle = async (item, mode = "preview") => {
    try {
      const bundle = filterBundleForAsset(await fetchBundle(), item.id);
      if (mode === "download") {
        downloadRegisterPDF(["hw_index", "hw"], bundle, `Hardware_Register_${item.asset_id || item.sl_no}.pdf`, { covers: false });
      } else {
        setPreviewTitle(`Hardware Register — ${item.asset_id || item.item_name}`);
        setPreviewUrl(registerBlobUrl(["hw_index", "hw"], bundle, { covers: false }));
      }
    } catch {
      toast.error("Could not prepare the register document");
    }
  };

  const markInactive = async (item) => {
    if (!window.confirm(`Mark "${item.item_name || item.asset_id}" as Inactive / Non-Working? It will move to Inactive Stock (history is preserved).`)) return;
    try {
      const { data } = await api.post(`/hardware/${item.id}/lifecycle`, { action: "mark_inactive" });
      setItems(prev => prev.map(x => x.id === data.id ? data : x));
      toast.success("Moved to Inactive Stock");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: hw }, { data: comps }] = await Promise.all([
        api.get("/hardware"),
        api.get("/registers/complaints"),
      ]);
      setItems(hw);
      setComplaints(comps || []);
    } catch {
      toast.error("Failed to load hardware register");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(it =>
      [it.item_name, it.manufacturer_model, it.serial_no, it.type_of_instrument, it.local_body_name]
        .some(v => (v || "").toLowerCase().includes(q))
    );
  }, [items, search]);

  // Reset paging index when the filtered set shrinks
  useEffect(() => {
    if (currentIdx >= filtered.length) setCurrentIdx(0);
  }, [filtered.length, currentIdx]);
  const currentItem = filtered[currentIdx] || null;

  const save = async (payload, pendingBills = []) => {
    try {
      let saved;
      if (editing?.id) {
        const { data } = await api.patch(`/hardware/${editing.id}`, payload);
        saved = data;
        setItems(prev => prev.map(x => x.id === data.id ? data : x));
        toast.success("Hardware item updated");
      } else {
        const { data } = await api.post("/hardware", payload);
        saved = data;
        setItems(prev => [...prev, data]);
        toast.success(`Hardware saved · Asset ID: ${data.asset_id || "—"}`);
      }
      // Upload any pending bills sequentially
      if (pendingBills.length && saved?.id) {
        for (const pb of pendingBills) {
          try {
            const data_b64 = await fileToBase64(pb.file);
            await api.post(`/hardware/${saved.id}/attachments`, {
              filename: pb.file.name,
              content_type: pb.file.type || "application/octet-stream",
              category: pb.category || "purchase_bill",
              label: pb.label || "",
              data_b64,
            });
          } catch (err) {
            toast.error(`Bill "${pb.file.name}" could not be uploaded: ${err?.response?.data?.detail || err.message}`);
          }
        }
        // Refresh the item so attachments show up
        try {
          const { data: fresh } = await api.get(`/hardware/${saved.id}`);
          setItems(prev => prev.map(x => x.id === fresh.id ? fresh : x));
          saved = fresh;
        } catch { /* ignore */ }
      }
      setEditing(null);
      // If a brand-new item was saved, show its QR code
      if (!editing?.id && saved) {
        setQrOf(saved);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (item) => {
    if (!window.confirm(`Delete "${item.item_name || 'this item'}" from the register? History is preserved and you can restore it from Inactive Stock → Deleted Assets.`)) return;
    try {
      await api.delete(`/hardware/${item.id}`);
      setItems(prev => prev.filter(x => x.id !== item.id));
      toast.success("Deleted (restorable from Inactive Stock)");
    } catch {
      toast.error("Delete failed");
    }
  };

  const saveMaint = async (payload) => {
    if (!maintOf) return;
    const body = { ...payload, hardware_id: maintOf.id };
    try {
      if (maintEditing?.id) {
        const { data } = await api.patch(`/registers/complaints/${maintEditing.id}`, body);
        setComplaints(prev => prev.map(c => c.id === data.id ? data : c));
        toast.success("Complaint entry updated");
      } else {
        const { data } = await api.post("/registers/complaints", body);
        setComplaints(prev => [...prev, data]);
        toast.success(`Complaint logged · ${data.comp_id || ""}`);
      }
      setMaintEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const delMaint = async (entry) => {
    if (!window.confirm(`Delete complaint ${entry.comp_id || "entry"}?`)) return;
    try {
      await api.delete(`/registers/complaints/${entry.id}`);
      const { data } = await api.get("/registers/complaints");
      setComplaints(data || []);
      toast.success("Complaint deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <Tabs value={subTab} onValueChange={setSubTab} className="space-y-5" data-testid="hardware-register-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <TabsList className="flex-wrap h-auto gap-1 justify-start">
          <TabsTrigger value="hardware" data-testid="subtab-hardware">Hardware / Stock</TabsTrigger>
          <TabsTrigger value="complaints" data-testid="subtab-complaints">Complaints</TabsTrigger>
          <TabsTrigger value="amc" data-testid="subtab-amc">AMC</TabsTrigger>
          <TabsTrigger value="connectivity" data-testid="subtab-connectivity">Connectivity</TabsTrigger>
          <TabsTrigger value="service" data-testid="subtab-service">Service</TabsTrigger>
          <TabsTrigger value="inactive" data-testid="subtab-inactive">Inactive Stock</TabsTrigger>
        </TabsList>
        <Button size="sm" onClick={() => setPrintAllOpen(true)} className="bg-brand-blue hover:bg-brand-blue/90 text-white" data-testid="print-all-btn">
          <Printer className="w-4 h-4 mr-1.5" /> Print All
        </Button>
      </div>

      <TabsContent value="hardware" className="mt-0 space-y-5">
      {/* Top gradient stripe + hero header */}
      <Card className="p-0 overflow-hidden">
        <div className="h-1.5 w-full flex" aria-hidden>
          <div className="flex-1 bg-brand-blue" />
          <div className="w-16 bg-brand-orange" />
          <div className="w-16 bg-emerald-500" />
        </div>
        <div className="p-6 bg-gradient-to-b from-sky-50/60 to-white">
          <p className="text-[11px] tracking-[0.14em] font-semibold text-brand-blue uppercase">
            Asset Management · Local Body
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold mt-1">Hardware Register</h2>
          <p className="text-sm text-muted-foreground max-w-2xl mt-1">
            A polished digital register for tracking equipment, warranty, AMC and service history.
          </p>

          {/* Pills row */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-3 py-1">
              <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden="true" className="shrink-0">
                <circle cx="4" cy="4" r="4" fill="currentColor" className="text-emerald-500" />
              </svg>
              <span>Active Register</span>
            </span>
            {filtered.length > 0 && (
              <span className="inline-flex items-center gap-1 text-xs font-medium bg-white border border-border rounded-full px-3 py-1 text-foreground/70" data-testid="hardware-record-pill">
                <span>Record {String(currentIdx + 1).padStart(2, "0")} of {String(filtered.length).padStart(2, "0")}</span>
              </span>
            )}
          </div>

          {/* Search + actions */}
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search item, manufacturer, serial number, bill number…"
                className="pl-9 bg-white h-11"
                data-testid="hardware-search"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => currentItem && printSingle(currentItem, "preview")}
              disabled={!currentItem}
              className="h-11"
              data-testid="hardware-print-current"
            >
              <Printer className="w-4 h-4 mr-1.5" /> Print view
            </Button>
            <Button
              onClick={() => setSearch("")}
              className="bg-brand-blue hover:bg-brand-blue/90 text-white h-11"
              data-testid="hardware-clear-search"
            >
              Clear search
            </Button>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPrintAllOpen(true)}
              data-testid="hardware-print-bulk"
            >
              <FileDown className="w-4 h-4 mr-1.5" /> Print registers
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setQrSheetOpen(true)}
              disabled={!items.length}
              data-testid="qr-sheet-btn"
            >
              <QrCode className="w-4 h-4 mr-1.5" /> QR Sheet
            </Button>
            <Button
              size="sm"
              onClick={() => setEditing({ ...EMPTY_ITEM, local_body_name: defaultLocalBody })}
              className="bg-brand-blue hover:bg-brand-blue/90 text-white"
              data-testid="hardware-add-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Add Hardware
            </Button>
          </div>
        </div>
      </Card>

      {loading ? (
        <Card className="p-10 text-center text-muted-foreground">Loading register…</Card>
      ) : (
        <>
          <RemindersAlert items={filtered} onOpenItem={(it) => setEditing({ ...EMPTY_ITEM, ...it })} />
          {!filtered.length ? (
            <Card className="p-10 text-center" data-testid="hardware-empty">
              <Cpu className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
              <h3 className="font-semibold">
                {items.length ? "No items match your search" : "No hardware logged yet"}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {items.length ? "Try a different keyword." : 'Click "Add Hardware" to log the first item.'}
              </p>
            </Card>
          ) : (
            <RecordView
              item={currentItem}
              index={currentIdx}
              total={filtered.length}
              complaints={complaints.filter(c => c.hardware_id === currentItem?.id)}
              onPrev={() => setCurrentIdx(i => Math.max(0, i - 1))}
              onNext={() => setCurrentIdx(i => Math.min(filtered.length - 1, i + 1))}
              onEdit={() => setEditing({ ...EMPTY_ITEM, ...currentItem })}
              onBills={() => setAttachOf(currentItem)}
              onMaintenance={() => setMaintOf(currentItem)}
              onPrint={() => printSingle(currentItem, "preview")}
              onDelete={() => del(currentItem)}
              onQr={() => setQrOf(currentItem)}
              onInactive={() => markInactive(currentItem)}
            />
          )}
        </>
      )}

      {/* Edit / Create dialog */}
      <HardwareItemDialog
        open={!!editing}
        item={editing}
        onClose={() => setEditing(null)}
        onSave={save}
      />

      {/* Maintenance dialog (unified Complaint Call Logging Register) */}
      <MaintenanceListDialog
        open={!!maintOf}
        item={maintOf}
        entries={complaints.filter(c => c.hardware_id === maintOf?.id)}
        onClose={() => { setMaintOf(null); setMaintEditing(null); }}
        onAdd={() => setMaintEditing({ ...EMPTY_MAINT })}
        onEdit={(e) => setMaintEditing({ ...EMPTY_MAINT, ...e })}
        onDelete={delMaint}
      />
      <MaintenanceEntryDialog
        open={!!maintEditing}
        entry={maintEditing}
        onClose={() => setMaintEditing(null)}
        onSave={saveMaint}
      />

      <AttachmentsDialog
        open={!!attachOf}
        item={attachOf}
        onClose={() => setAttachOf(null)}
        onChange={(next) => {
          setItems(prev => prev.map(x => x.id === next.id ? next : x));
          setAttachOf(next);
        }}
      />

      <QrLabelDialog
        open={!!qrOf}
        item={qrOf}
        onClose={() => setQrOf(null)}
      />
      <QrSheetDialog
        open={qrSheetOpen}
        items={items}
        onClose={() => setQrSheetOpen(false)}
      />
      <GovFooterNote />
      </TabsContent>

      <TabsContent value="complaints" className="mt-0"><ComplaintRegisterTab /></TabsContent>
      <TabsContent value="amc" className="mt-0"><AmcRegisterTab /></TabsContent>
      <TabsContent value="connectivity" className="mt-0"><ConnectivityRegisterTab /></TabsContent>
      <TabsContent value="service" className="mt-0"><ServiceRegisterTab /></TabsContent>
      <TabsContent value="inactive" className="mt-0"><InactiveStockPanel /></TabsContent>

      <PrintAllDialog open={printAllOpen} onClose={() => setPrintAllOpen(false)} />
      <PdfPreviewDialog url={previewUrl} onClose={() => setPreviewUrl("")} title={previewTitle} />
    </Tabs>
  );
}

/* ---------- Item dialog ---------- */
function HardwareItemDialog({ open, item, onClose, onSave }) {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY_ITEM);
  const [pendingBills, setPendingBills] = useState([]); // [{ file, category, label }]
  const pendingFileRef = useRef(null);

  useEffect(() => {
    if (open) {
      const base = { ...EMPTY_ITEM, ...(item || {}) };
      if (!base.institution_type) base.institution_type = "local_body";
      if (!base.local_body_name && user?.local_body_name) base.local_body_name = user.local_body_name;
      if (!base.institution_name) base.institution_name = base.local_body_name || "";
      setForm(base);
      setPendingBills([]);
    }
  }, [open, item, user]);

  const activeCategory = getCategory(form.category);

  const submit = (e) => {
    e.preventDefault();
    if (!form.institution_type) {
      toast.error("Please select an institution type first");
      return;
    }
    if (!form.institution_name?.trim()) {
      toast.error("Institution name is required");
      return;
    }
    if (!form.subtype && !form.item_name?.trim()) {
      toast.error("Please pick a Type of Instrument in section 2");
      return;
    }
    const payload = {};
    Object.keys(EMPTY_ITEM).forEach(k => { payload[k] = form[k] || ""; });
    // Auto-derive item_name from Brand + Subtype so registers stay consistent
    if (!payload.item_name?.trim()) {
      payload.item_name = [payload.brand, payload.subtype].filter(Boolean).join(" ") || payload.subtype || payload.category || "";
    }
    if (payload.subtype && !payload.type_of_instrument) payload.type_of_instrument = payload.subtype;
    if (payload.institution_type === "local_body" && payload.institution_name) payload.local_body_name = payload.institution_name;
    onSave(payload, pendingBills);
  };

  const addPendingBill = (file) => {
    if (!file) return;
    const allowed = ["application/pdf", "image/png", "image/jpeg", "image/jpg", "image/webp", "image/heic"];
    if (!allowed.includes((file.type || "").toLowerCase())) {
      toast.error("Only PDF, PNG, JPG, WEBP or HEIC files are allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Max 5 MB.");
      return;
    }
    setPendingBills(prev => [...prev, { file, category: "purchase_bill", label: "" }]);
    if (pendingFileRef.current) pendingFileRef.current.value = "";
  };

  const field = (name, label, opts = {}) => (
    <div>
      <Label htmlFor={`hw-${name}`} className="text-xs">{label}</Label>
      {opts.multiline ? (
        <Textarea
          id={`hw-${name}`}
          rows={opts.rows || 2}
          value={form[name] || ""}
          onChange={e => setForm(f => ({ ...f, [name]: e.target.value }))}
          data-testid={`hw-field-${name}`}
        />
      ) : opts.type === "date" ? (
        <DatePicker
          value={form[name] || ""}
          onChange={v => setForm(f => ({ ...f, [name]: v }))}
          testid={`hw-field-${name}`}
        />
      ) : (
        <Input
          id={`hw-${name}`}
          type={opts.type || "text"}
          value={form[name] || ""}
          onChange={e => setForm(f => ({ ...f, [name]: e.target.value }))}
          placeholder={opts.placeholder}
          data-testid={`hw-field-${name}`}
        />
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="hardware-item-dialog">
        <DialogHeader>
          <DialogTitle>{item?.id ? "Edit Hardware Item" : "Add Hardware Item"}</DialogTitle>
          <DialogDescription>Enter the institution, item, supply, warranty and AMC details. Bills can be attached before saving.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          {/* Step 1: Institution */}
          <section>
            <h4 className="text-sm font-semibold mb-2 text-brand-blue">1 · Which institution owns this asset?</h4>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Institution type</Label>
                <div className="flex gap-2 mt-1">
                  {INSTITUTION_TYPES.map(t => (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, institution_type: t.value }))}
                      className={`px-3 py-1.5 text-xs rounded-md border transition ${
                        form.institution_type === t.value
                          ? "bg-brand-blue text-white border-brand-blue"
                          : "bg-white border-border text-foreground hover:bg-muted/50"
                      }`}
                      data-testid={`hw-institution-${t.value}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs" htmlFor="hw-institution-name">
                  {form.institution_type === "other_office" ? "Office name" : "Local body name"}
                </Label>
                <Input
                  id="hw-institution-name"
                  value={form.institution_name}
                  onChange={e => setForm(f => ({ ...f, institution_name: e.target.value }))}
                  placeholder={form.institution_type === "other_office" ? "e.g. State IT Cell" : "e.g. Thiruvananthapuram Corporation"}
                  data-testid="hw-field-institution_name"
                />
              </div>
            </div>
          </section>

          {/* Step 2: Category / Subtype / Brand */}
          <section>
            <h4 className="text-sm font-semibold mb-2 text-brand-blue">2 · Category, type & brand</h4>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Category</Label>
                <Select
                  value={form.category}
                  onValueChange={v => setForm(f => ({ ...f, category: v, subtype: "", brand: "" }))}
                >
                  <SelectTrigger data-testid="hw-field-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Type of Instrument</Label>
                <Select
                  value={form.subtype}
                  onValueChange={v => setForm(f => ({ ...f, subtype: v }))}
                  disabled={!activeCategory}
                >
                  <SelectTrigger data-testid="hw-field-subtype"><SelectValue placeholder={activeCategory ? "Select type" : "Choose category first"} /></SelectTrigger>
                  <SelectContent>
                    {(activeCategory?.subtypes || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Brand</Label>
                <Select
                  value={form.brand}
                  onValueChange={v => setForm(f => ({ ...f, brand: v }))}
                  disabled={!activeCategory}
                >
                  <SelectTrigger data-testid="hw-field-brand"><SelectValue placeholder={activeCategory ? "Select brand" : "Choose category first"} /></SelectTrigger>
                  <SelectContent>
                    {(activeCategory?.brands || []).map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>

          {/* Step 3: Item & Supply */}
          <section>
            <h4 className="text-sm font-semibold mb-2 text-brand-blue">3 · Item &amp; Supply</h4>
            <p className="text-xs text-muted-foreground mb-2">Item name is auto-set from the Brand + Type you selected above. Fill in what changes per unit.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {field("manufacturer_model", "Manufacturer / Make and Model")}
              {field("specifications", "Specifications", { multiline: true, rows: 2, placeholder: "One per line, or comma-separated · e.g. Core i5 · 16 GB RAM · 512 GB SSD" })}
              <div>
                {field("date_of_supply", "Date of Supply", { type: "date" })}
              </div>
              {field("serial_no", "Serial Number (must be unique)")}
            </div>
          </section>

          {/* Step 4: Vendor & Contact */}
          <section>
            <h4 className="text-sm font-semibold mb-2 text-brand-blue">4 · Vendor &amp; Contact Details</h4>
            <div className="grid sm:grid-cols-2 gap-3">
              {field("vendor_name", "Vendor Name")}
              {field("vendor_contact_person", "Contact Person")}
              {field("vendor_phone", "Contact Number")}
              {field("vendor_email", "Email", { type: "email" })}
              <div className="sm:col-span-2">
                {field("vendor_address", "Address", { multiline: true, rows: 2 })}
              </div>
              {field("vendor_gst", "GST Number")}
            </div>
          </section>

          {/* Step 5: Warranty */}
          <section>
            <h4 className="text-sm font-semibold mb-2 text-brand-blue">5 · Warranty &amp; Condition</h4>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Warranty Period</Label>
                <Select
                  value={form.warranty_period}
                  onValueChange={v => {
                    setForm(f => {
                      const next = { ...f, warranty_period: v };
                      // auto-calc end date if start is set
                      const y = parseInt(v, 10);
                      if (!isNaN(y) && f.initial_warranty_from) {
                        const from = new Date(f.initial_warranty_from);
                        if (!isNaN(from.getTime())) {
                          const to = new Date(from);
                          to.setFullYear(to.getFullYear() + y);
                          to.setDate(to.getDate() - 1);
                          next.initial_warranty_to = to.toISOString().slice(0, 10);
                        }
                      }
                      return next;
                    });
                  }}
                >
                  <SelectTrigger data-testid="hw-field-warranty_period"><SelectValue placeholder="Select period" /></SelectTrigger>
                  <SelectContent>
                    {["1", "2", "3", "4", "5"].map(y => (
                      <SelectItem key={y} value={y}>{y} Year{y === "1" ? "" : "s"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs" htmlFor="hw-initial_warranty_from">Warranty From</Label>
                <Input
                  id="hw-initial_warranty_from"
                  type="date"
                  value={form.initial_warranty_from}
                  onChange={e => {
                    const v = e.target.value;
                    setForm(f => {
                      const next = { ...f, initial_warranty_from: v };
                      const y = parseInt(f.warranty_period, 10);
                      if (!isNaN(y) && v) {
                        const from = new Date(v);
                        if (!isNaN(from.getTime())) {
                          const to = new Date(from);
                          to.setFullYear(to.getFullYear() + y);
                          to.setDate(to.getDate() - 1);
                          next.initial_warranty_to = to.toISOString().slice(0, 10);
                        }
                      }
                      return next;
                    });
                  }}
                  data-testid="hw-field-initial_warranty_from"
                />
              </div>
              {field("initial_warranty_to", "Warranty To (auto)", { type: "date" })}
              {field("present_condition", "Present Condition")}
              {field("assigned_user", "Assigned User / Department")}
              {field("bill_no_date", "Bill No. & Date, Amount")}
              {field("cd_docs", "Documents Supplied")}
              <div className="sm:col-span-3">
                {field("remarks", "Remarks", { multiline: true, rows: 2 })}
              </div>
            </div>
          </section>

          {/* Step 6: AMC */}
          <section>
            <h4 className="text-sm font-semibold mb-2 text-brand-blue">6 · AMC Particulars</h4>
            <div className="grid sm:grid-cols-2 gap-3">
              {field("amc_from", "AMC From", { type: "date" })}
              {field("amc_to", "AMC To", { type: "date" })}
              {field("service_provider", "Service Provider (Name, Address, Phone)", { multiline: true })}
              {field("amc_no_amount", "AMC No. + Amount Paid with Date")}
              {field("initials_of_secretary", "Initials of Secretary")}
              {field("verified_sa", "Verified (SA)")}
            </div>
          </section>

          {/* Step 6: Attach bills (optional) */}
          {!item?.id && (
            <section>
              <h4 className="text-sm font-semibold mb-2 text-brand-blue">7 · Attach bills (optional)</h4>
              <p className="text-xs text-muted-foreground mb-2">Purchase bill, warranty card or AMC contract. PDF/PNG/JPG up to 5 MB. Files upload right after the item is saved.</p>
              <div className="flex flex-wrap gap-2 items-center">
                <input
                  ref={pendingFileRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,application/pdf,image/*"
                  className="hidden"
                  onChange={e => addPendingBill(e.target.files?.[0])}
                  data-testid="hw-pending-bill-input"
                />
                <Button type="button" variant="outline" size="sm" onClick={() => pendingFileRef.current?.click()} data-testid="hw-pending-bill-btn">
                  <Upload className="w-4 h-4 mr-1.5" /> Add bill
                </Button>
                {pendingBills.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {pendingBills.length} file{pendingBills.length === 1 ? "" : "s"} ready
                  </span>
                )}
              </div>
              {pendingBills.length > 0 && (
                <div className="mt-2 space-y-2">
                  {pendingBills.map((pb, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded border border-border bg-muted/30 text-xs">
                      <FileText className="w-4 h-4 text-brand-blue shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{pb.file.name}</div>
                        <div className="flex gap-2 mt-1 flex-wrap">
                          <Select value={pb.category} onValueChange={v => setPendingBills(prev => prev.map((x, j) => j === i ? { ...x, category: v } : x))}>
                            <SelectTrigger className="h-7 text-xs w-40"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="purchase_bill">Purchase Bill</SelectItem>
                              <SelectItem value="warranty">Warranty</SelectItem>
                              <SelectItem value="amc">AMC / Service</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            value={pb.label}
                            onChange={e => setPendingBills(prev => prev.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
                            placeholder="Label (optional)"
                            className="h-7 text-xs flex-1"
                          />
                        </div>
                      </div>
                      <button type="button" onClick={() => setPendingBills(prev => prev.filter((_, j) => j !== i))} className="text-red-600 hover:text-red-700 p-1" aria-label="Remove">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-brand-blue hover:bg-brand-blue/90 text-white" data-testid="hardware-item-save">
              {item?.id ? "Save changes" : "Save & get Asset ID"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Maintenance list dialog (unified Complaint Call Logging Register) ---------- */
function MaintenanceListDialog({ open, item, entries, onClose, onAdd, onEdit, onDelete }) {
  const rows = entries || [];
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="hardware-maint-dialog">
        <DialogHeader>
          <DialogTitle>Complaint &amp; Maintenance Log — {item?.item_name || "Item"}</DialogTitle>
          <DialogDescription>
            Unified Complaint Call Logging Register (Annexure-2 · IKM/TSIM/FMT/19) for asset {item?.asset_id || ""}.
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end mb-3">
          <Button size="sm" onClick={onAdd} className="bg-brand-blue hover:bg-brand-blue/90 text-white" data-testid="hardware-maint-add">
            <Plus className="w-4 h-4 mr-1.5" /> Log Complaint
          </Button>
        </div>
        {!rows.length ? (
          <div className="text-center text-sm text-muted-foreground py-8">No complaints logged yet.</div>
        ) : (
          <div className="space-y-3">
            {rows.map(e => (
              <Card key={e.id} className="p-3" data-testid={`maint-entry-${e.id}`}>
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Badge variant="outline" className="text-[10px] font-mono">{e.comp_id || `#${e.sl_no}`}</Badge>
                      <span className="text-xs text-muted-foreground">Reported: {e.reporting_date || "—"}</span>
                      {e.solved && (
                        <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${e.solved === "Yes" ? "bg-emerald-500/10 text-emerald-700" : "bg-red-500/10 text-red-600"}`}>
                          Solved: {e.solved}
                        </span>
                      )}
                      {e.tracking_number && <span className="text-xs text-muted-foreground">Tracking: {e.tracking_number}</span>}
                    </div>
                    <p className="text-sm font-medium">{e.complaint_remarks || "—"}</p>
                    {e.service_remarks && <p className="text-xs mt-1"><span className="font-medium">Service:</span> {e.service_remarks}</p>}
                    {e.service_date && <p className="text-xs mt-0.5"><span className="font-medium">Service date:</span> {e.service_date}</p>}
                    {e.technician_contact && <p className="text-xs text-muted-foreground mt-1">— {e.technician_contact}</p>}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" onClick={() => onEdit(e)} data-testid={`maint-edit-${e.id}`}><PenSquare className="w-4 h-4" /></Button>
                    <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => onDelete(e)} data-testid={`maint-del-${e.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Maintenance entry dialog ---------- */
function MaintenanceEntryDialog({ open, entry, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY_MAINT);
  useEffect(() => {
    if (open) setForm({ ...EMPTY_MAINT, ...(entry || {}) });
  }, [open, entry]);

  const submit = (e) => {
    e.preventDefault();
    onSave({ ...form });
  };
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="hardware-maint-entry-dialog">
        <DialogHeader>
          <DialogTitle>{entry?.id ? `Edit complaint ${entry.comp_id || ""}` : "Log complaint / maintenance"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Complaint Remarks</Label>
            <Textarea rows={2} value={form.complaint_remarks} onChange={e => setForm(f => ({ ...f, complaint_remarks: e.target.value }))} data-testid="maint-field-complaint" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Reporting Date</Label>
              <DatePicker value={form.reporting_date} onChange={v => setForm(f => ({ ...f, reporting_date: v }))} testid="maint-field-date" />
            </div>
            <div>
              <Label className="text-xs">Service Date</Label>
              <DatePicker value={form.service_date} onChange={v => setForm(f => ({ ...f, service_date: v }))} testid="maint-field-service-date" />
            </div>
            <div>
              <Label className="text-xs">Solved Status</Label>
              <Select value={form.solved} onValueChange={v => setForm(f => ({ ...f, solved: v }))}>
                <SelectTrigger data-testid="maint-field-solved"><SelectValue placeholder="Yes / No" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Tracking Number</Label>
              <Input value={form.tracking_number} onChange={e => setForm(f => ({ ...f, tracking_number: e.target.value }))} data-testid="maint-field-tracking" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Service Remarks</Label>
            <Textarea rows={2} value={form.service_remarks} onChange={e => setForm(f => ({ ...f, service_remarks: e.target.value }))} data-testid="maint-field-tech" />
          </div>
          <div>
            <Label className="text-xs">Name of Technician &amp; Contact Number</Label>
            <Textarea rows={2} value={form.technician_contact} onChange={e => setForm(f => ({ ...f, technician_contact: e.target.value }))} data-testid="maint-field-tech-sign" />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-brand-blue hover:bg-brand-blue/90 text-white" data-testid="maint-save-btn">
              {entry?.id ? "Save changes" : "Log complaint"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Attachments dialog (bills / warranty / AMC docs) ---------- */
const ATTACH_CATEGORIES = [
  { value: "purchase_bill", label: "Purchase Bill" },
  { value: "warranty", label: "Warranty" },
  { value: "amc", label: "AMC / Service" },
  { value: "other", label: "Other" },
];
const ATTACH_ALLOWED = ["application/pdf", "image/png", "image/jpeg", "image/jpg", "image/webp", "image/heic"];
const ATTACH_MAX_MB = 5;

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const res = reader.result || "";
      const b64 = String(res).split(",")[1] || "";
      resolve(b64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function humanSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function AttachmentsDialog({ open, item, onClose, onChange }) {
  const [category, setCategory] = useState("purchase_bill");
  const [label, setLabel] = useState("");
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewName, setPreviewName] = useState("");
  const [previewType, setPreviewType] = useState("");
  const fileRef = useRef(null);

  useEffect(() => { if (open) { setCategory("purchase_bill"); setLabel(""); } }, [open]);
  // Revoke any preview URLs on close/change
  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  if (!item) return null;
  const attachments = item.attachments || [];

  const upload = async (file) => {
    if (!file) return;
    if (!ATTACH_ALLOWED.includes((file.type || "").toLowerCase())) {
      toast.error("Only PDF, PNG, JPG, WEBP or HEIC files are allowed");
      return;
    }
    if (file.size > ATTACH_MAX_MB * 1024 * 1024) {
      toast.error(`File too large. Max ${ATTACH_MAX_MB} MB.`);
      return;
    }
    setUploading(true);
    try {
      const data_b64 = await fileToBase64(file);
      await api.post(`/hardware/${item.id}/attachments`, {
        filename: file.name,
        content_type: file.type || "application/octet-stream",
        category,
        label,
        data_b64,
      });
      const { data: fresh } = await api.get(`/hardware/${item.id}`);
      onChange(fresh);
      toast.success("Uploaded");
      setLabel("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const del = async (a) => {
    if (!window.confirm(`Delete "${a.filename}"?`)) return;
    try {
      await api.delete(`/hardware/${item.id}/attachments/${a.id}`);
      const { data: fresh } = await api.get(`/hardware/${item.id}`);
      onChange(fresh);
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const fetchBlob = async (a) => {
    const res = await api.get(`/hardware/${item.id}/attachments/${a.id}`, { responseType: "blob" });
    return res.data;
  };

  const view = async (a) => {
    try {
      const blob = await fetchBlob(a);
      const url = URL.createObjectURL(blob);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
      setPreviewName(a.filename);
      setPreviewType(a.content_type);
    } catch {
      toast.error("Unable to load preview");
    }
  };

  const download = async (a) => {
    try {
      const blob = await fetchBlob(a);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = a.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const printFile = async (a) => {
    try {
      const blob = await fetchBlob(a);
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank");
      if (win) {
        // Attempt to trigger print after load
        win.addEventListener("load", () => win.print(), { once: true });
      }
    } catch {
      toast.error("Print failed");
    }
  };

  const isImage = (t) => (t || "").startsWith("image/");
  const isPdf = (t) => (t || "").includes("pdf");

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="hardware-attachments-dialog">
        <DialogHeader>
          <DialogTitle>Bills & Documents — {item.item_name || "Item"}</DialogTitle>
          <DialogDescription>
            Attach purchase bill, warranty card, AMC contract or any related document. PDF, PNG, JPG, WEBP or HEIC — up to {ATTACH_MAX_MB} MB per file.
          </DialogDescription>
        </DialogHeader>

        {/* Uploader */}
        <Card className="p-4 bg-muted/30 border-dashed">
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="attachment-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ATTACH_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Label (optional)</Label>
              <Input value={label} onChange={e => setLabel(e.target.value)} placeholder='e.g. "Bill No 45 dated 10-May-2024"' data-testid="attachment-label" />
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,application/pdf,image/*"
              onChange={e => upload(e.target.files?.[0])}
              className="hidden"
              data-testid="attachment-file-input"
            />
            <Button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="bg-brand-blue hover:bg-brand-blue/90 text-white"
              data-testid="attachment-upload-btn"
            >
              <Upload className="w-4 h-4 mr-1.5" />
              {uploading ? "Uploading…" : "Choose file to upload"}
            </Button>
            <span className="text-xs text-muted-foreground">Max {ATTACH_MAX_MB} MB • PDF / PNG / JPG / WEBP / HEIC</span>
          </div>
        </Card>

        {/* List */}
        {!attachments.length ? (
          <div className="text-center text-sm text-muted-foreground py-8" data-testid="attachments-empty">
            <Paperclip className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
            No files uploaded yet.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3 mt-2">
            {attachments.map(a => {
              const cat = ATTACH_CATEGORIES.find(c => c.value === a.category);
              const Icon = isImage(a.content_type) ? ImageIcon : FileText;
              return (
                <Card key={a.id} className="p-3" data-testid={`attachment-${a.id}`}>
                  <div className="flex items-start gap-2">
                    <div className="w-9 h-9 rounded-md bg-brand-blue/10 text-brand-blue flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        {cat && <Badge className="bg-brand-blue/10 text-brand-blue hover:bg-brand-blue/10 text-[10px]">{cat.label}</Badge>}
                        <span className="text-[11px] text-muted-foreground">{humanSize(a.size || 0)}</span>
                      </div>
                      <p className="text-sm font-medium truncate" title={a.filename}>{a.filename}</p>
                      {a.label && <p className="text-xs text-muted-foreground">{a.label}</p>}
                      <div className="flex flex-wrap gap-1 mt-2">
                        <Button variant="outline" size="sm" onClick={() => view(a)} data-testid={`attachment-view-${a.id}`}>
                          <Eye className="w-3.5 h-3.5 mr-1" /> View
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => download(a)} data-testid={`attachment-download-${a.id}`}>
                          <Download className="w-3.5 h-3.5 mr-1" /> Download
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => printFile(a)} data-testid={`attachment-print-${a.id}`}>
                          <Printer className="w-3.5 h-3.5 mr-1" /> Print
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => del(a)} data-testid={`attachment-delete-${a.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Inline preview */}
        {previewUrl && (
          <Card className="p-3 mt-3" data-testid="attachment-preview-card">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold truncate flex-1">{previewName}</p>
              <Button variant="ghost" size="sm" onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(""); }}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            {isImage(previewType) ? (
              <img src={previewUrl} alt={previewName} className="max-h-[65vh] mx-auto object-contain" />
            ) : isPdf(previewType) ? (
              <iframe src={previewUrl} title={previewName} className="w-full h-[65vh] border rounded" />
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">Preview not available — use Download.</p>
            )}
          </Card>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ---------- Reminder badges shown next to item title ---------- */
function ReminderBadges({ item }) {
  const amc = reminderStatus(item.amc_to);
  const wr = reminderStatus(item.initial_warranty_to);
  const chips = [];
  if (amc.status === "expired") chips.push({ tone: "red", label: reminderLabel("AMC", amc) });
  else if (amc.status === "expiring_soon") chips.push({ tone: "amber", label: reminderLabel("AMC", amc) });
  if (wr.status === "expired") chips.push({ tone: "red", label: reminderLabel("Warranty", wr) });
  else if (wr.status === "expiring_soon") chips.push({ tone: "amber", label: reminderLabel("Warranty", wr) });
  if (!chips.length) return null;
  return (
    <>
      {chips.map((c, i) => (
        <Badge
          key={i}
          className={
            c.tone === "red"
              ? "bg-red-500/10 text-red-600 hover:bg-red-500/10 border border-red-200 text-[10px]"
              : "bg-amber-500/10 text-amber-700 hover:bg-amber-500/10 border border-amber-200 text-[10px]"
          }
          data-testid={`hardware-reminder-${c.tone === "red" ? "expired" : "soon"}`}
        >
          {c.tone === "red" ? <AlertTriangle className="w-3 h-3 mr-1" /> : <Clock className="w-3 h-3 mr-1" />}
          {c.label}
        </Badge>
      ))}
    </>
  );
}

/* ---------- Reminders alert card shown above the list ---------- */
function RemindersAlert({ items, onOpenItem }) {
  const rows = useMemo(() => {
    const out = [];
    (items || []).forEach(item => {
      const amc = reminderStatus(item.amc_to);
      const wr = reminderStatus(item.initial_warranty_to);
      if (["expired", "expiring_soon"].includes(amc.status)) {
        out.push({ item, kind: "AMC", ...amc });
      }
      if (["expired", "expiring_soon"].includes(wr.status)) {
        out.push({ item, kind: "Warranty", ...wr });
      }
    });
    // sort: expired first (most overdue), then soonest to expire
    return out.sort((a, b) => {
      if (a.status !== b.status) return a.status === "expired" ? -1 : 1;
      return (a.delta ?? 0) - (b.delta ?? 0);
    });
  }, [items]);

  if (!rows.length) return null;
  const expiredCount = rows.filter(r => r.status === "expired").length;
  const soonCount = rows.length - expiredCount;

  return (
    <Card className="p-4 border-amber-200 bg-amber-50/60" data-testid="hardware-reminders-alert">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-lg bg-amber-500/15 text-amber-700 flex items-center justify-center shrink-0">
          <AlertTriangle className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-[240px]">
          <h3 className="font-semibold text-amber-900">
            {expiredCount ? `${expiredCount} expired` : ""}
            {expiredCount && soonCount ? " · " : ""}
            {soonCount ? `${soonCount} expiring within ${REMINDER_WINDOW_DAYS} days` : ""}
          </h3>
          <p className="text-xs text-amber-800/80 mt-0.5">
            Renew AMC contracts and warranty coverage before the deadline to avoid lapses.
          </p>
          <div className="mt-3 space-y-1.5">
            {rows.slice(0, 6).map((r, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onOpenItem(r.item)}
                className="w-full text-left text-xs bg-white/70 hover:bg-white rounded-md px-3 py-1.5 border border-amber-200/70 flex items-center gap-2 transition"
                data-testid={`hardware-reminder-row-${i}`}
              >
                {r.status === "expired"
                  ? <AlertTriangle className="w-3.5 h-3.5 text-red-600 shrink-0" />
                  : <Clock className="w-3.5 h-3.5 text-amber-600 shrink-0" />}
                <span className="font-semibold truncate">{r.item.item_name || "Untitled"}</span>
                <span className="text-muted-foreground truncate">— {reminderLabel(r.kind, r)}</span>
              </button>
            ))}
            {rows.length > 6 && (
              <p className="text-[11px] text-amber-800/80">+ {rows.length - 6} more…</p>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}


/* ---------- Rich single-record view ---------- */

function formatDateNice(s) {
  if (!s) return "";
  const d = parseIsoDate(s);
  if (!d) return s;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function yearsBetween(fromStr, toStr) {
  const a = parseIsoDate(fromStr);
  const b = parseIsoDate(toStr);
  if (!a || !b) return null;
  const years = (b.getTime() - a.getTime()) / (365.25 * 86400000);
  return Math.max(0, Math.round(years));
}

function warrantyProgress(fromStr, toStr) {
  const a = parseIsoDate(fromStr);
  const b = parseIsoDate(toStr);
  if (!a || !b || b.getTime() <= a.getTime()) return 0;
  const now = Date.now();
  const pct = ((now - a.getTime()) / (b.getTime() - a.getTime())) * 100;
  return Math.max(0, Math.min(100, pct));
}

function splitSpecs(text) {
  if (!text) return [];
  return String(text).split(/\r?\n|,|·|;|\|/).map(s => s.trim()).filter(Boolean);
}

function amcRecordStatus(item) {
  const amc = reminderStatus(item.amc_to);
  if (amc.status === "expired") return "AMC expired — renew soon";
  if (amc.status === "expiring_soon") return `AMC expires in ${amc.delta} day${amc.delta === 1 ? "" : "s"}`;
  if (amc.status === "ok") return "Under active AMC";
  return "Ready for maintenance entries";
}

function RecordView({ item, index, total, complaints = [], onPrev, onNext, onEdit, onBills, onMaintenance, onPrint, onDelete, onQr, onInactive }) {
  if (!item) return null;
  const status = item.status || "Active";
  return (
    <div className="space-y-4" data-testid="hardware-record-view">
      {/* Prev/Next navigation strip */}
      <div className="flex items-center justify-between gap-2">
        <Button variant="outline" size="sm" onClick={onPrev} disabled={index === 0} data-testid="record-prev">
          <ChevronLeft className="w-4 h-4 mr-1" /> Previous
        </Button>
        <div className="text-xs text-muted-foreground">
          Record <strong>{String(index + 1).padStart(2, "0")}</strong> of {String(total).padStart(2, "0")}
        </div>
        <Button variant="outline" size="sm" onClick={onNext} disabled={index >= total - 1} data-testid="record-next">
          Next <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      {/* Equipment + Warranty split */}
      <div className="grid lg:grid-cols-3 gap-4">
        <EquipmentCard item={item} />
        <WarrantyHealthCard item={item} />
      </div>

      {/* Technical specifications */}
      <TechnicalSpecsCard item={item} />

      {/* AMC particulars */}
      <AmcParticularsCard item={item} />

      {/* Maintenance history (unified complaint register) */}
      <MaintenanceHistoryCard item={item} entries={complaints} />

      {/* Audit trail */}
      <AuditTrailCard item={item} />

      {/* Record actions */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-2 justify-end">
          {status !== "Active" && (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2.5 py-1 border bg-amber-500/10 text-amber-700 border-amber-200 mr-auto" data-testid="record-status-badge">
              <PackageX className="w-3.5 h-3.5" /> {status} — manage in Inactive Stock
            </span>
          )}
          {status === "Active" && (
            <Button variant="outline" size="sm" onClick={onInactive} data-testid="record-mark-inactive">
              <PackageX className="w-4 h-4 mr-1.5" /> Mark Inactive
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onBills} data-testid="record-bills">
            <Paperclip className="w-4 h-4 mr-1.5" /> Bills{(item.attachments || []).length ? ` (${(item.attachments || []).length})` : ""}
          </Button>
          <Button variant="outline" size="sm" onClick={onMaintenance} data-testid="record-maintenance">
            <Wrench className="w-4 h-4 mr-1.5" /> Complaints
          </Button>
          <Button variant="outline" size="sm" onClick={onPrint} data-testid="record-print">
            <Printer className="w-4 h-4 mr-1.5" /> Print
          </Button>
          <Button variant="outline" size="sm" onClick={onQr} data-testid="record-qr">
            <QrCode className="w-4 h-4 mr-1.5" /> Asset QR
          </Button>
          <Button variant="outline" size="sm" onClick={onEdit} data-testid="record-edit">
            <PenSquare className="w-4 h-4 mr-1.5" /> Edit
          </Button>
          <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={onDelete} data-testid="record-delete">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </Card>
    </div>
  );
}

function InfoTile({ label, value, testid }) {
  return (
    <div className="bg-white border border-border rounded-lg p-3" data-testid={testid}>
      <div className="text-[10px] tracking-[0.12em] font-semibold text-muted-foreground uppercase">{label}</div>
      <div className="text-sm font-semibold mt-1 break-words">{value || "—"}</div>
    </div>
  );
}

function EquipmentCard({ item }) {
  return (
    <Card className="lg:col-span-2 p-6 relative overflow-hidden" data-testid="record-equipment-card">
      <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-brand-blue/5 -mr-16 -mt-16 pointer-events-none" aria-hidden />
      <div className="absolute top-4 right-8 w-16 h-16 rounded-full bg-brand-orange/10 pointer-events-none" aria-hidden />

      <div className="relative flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-brand-blue/10 text-brand-blue flex items-center justify-center shrink-0">
          <Package className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] tracking-[0.14em] font-semibold text-muted-foreground uppercase">
            Equipment · Sl. No. {item.sl_no || "—"}
          </p>
          {item.asset_id && (
            <p className="text-brand-blue font-mono text-sm font-bold mt-0.5 tracking-wide" data-testid="record-asset-id">
              Asset ID · {item.asset_id}
            </p>
          )}
          <h3 className="text-2xl font-bold mt-0.5 break-words">{item.item_name || "Untitled Item"}</h3>
          <div className="flex flex-wrap gap-2 mt-2">
            {item.brand && (
              <Badge className="bg-brand-blue/10 text-brand-blue hover:bg-brand-blue/10 border-0 text-[11px]">{item.brand}</Badge>
            )}
            {item.subtype && (
              <Badge className="bg-brand-orange/10 text-brand-orange hover:bg-brand-orange/10 border-0 text-[11px]">{item.subtype}</Badge>
            )}
            {!item.subtype && item.type_of_instrument && (
              <Badge className="bg-brand-orange/10 text-brand-orange hover:bg-brand-orange/10 border-0 text-[11px]">{item.type_of_instrument}</Badge>
            )}
            {item.present_condition && (
              <Badge className="bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/10 border-0 text-[11px]">{item.present_condition}</Badge>
            )}
            <ReminderBadges item={item} />
          </div>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-2.5 relative">
        <InfoTile label="Institution" value={item.institution_name || item.local_body_name} testid="tile-institution" />
        <InfoTile label={item.institution_type === "other_office" ? "Type" : "Local Body"} value={item.institution_type === "other_office" ? "Other Office" : (item.local_body_name || item.institution_name)} testid="tile-institution-type" />
        <InfoTile label="Date of Supply" value={formatDateNice(item.date_of_supply)} testid="tile-date-supply" />
        <InfoTile label="Vendor" value={item.vendor_name || item.supplied_by} testid="tile-vendor" />
        <InfoTile label="Serial Number" value={item.serial_no} testid="tile-serial" />
        <InfoTile label="Bill Reference" value={item.bill_no_date} testid="tile-bill" />
      </div>
    </Card>
  );
}

function WarrantyHealthCard({ item }) {
  const wr = reminderStatus(item.initial_warranty_to);
  const isValid = wr.status === "ok" || wr.status === "expiring_soon";
  const years = yearsBetween(item.initial_warranty_from, item.initial_warranty_to);
  const pct = warrantyProgress(item.initial_warranty_from, item.initial_warranty_to);
  const badgeClass = isValid
    ? "bg-emerald-500/10 text-emerald-700 border-emerald-200"
    : "bg-red-500/10 text-red-700 border-red-200";
  const badgeLabel = wr.status === "expired" ? "Expired" : wr.status === "expiring_soon" ? "Ending soon" : wr.status === "ok" ? "Valid" : "—";
  return (
    <Card className="p-6" data-testid="record-warranty-card">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] tracking-[0.14em] font-semibold text-muted-foreground uppercase">Warranty health</p>
          <div className="h-1 w-10 bg-brand-blue mt-1 rounded" aria-hidden />
        </div>
        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2.5 py-1 border ${badgeClass}`} data-testid="warranty-status-badge">
          {isValid ? <ShieldCheck className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />}
          {badgeLabel}
        </span>
      </div>

      <div className="mt-4">
        <div className="text-3xl font-bold" data-testid="warranty-years">
          {years !== null ? `${years} Year${years === 1 ? "" : "s"}` : "—"}
        </div>
        <p className="text-[10px] tracking-[0.14em] font-semibold text-muted-foreground uppercase mt-1">
          Initial warranty period
        </p>
      </div>

      <div className="mt-4">
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{formatDateNice(item.initial_warranty_from) || "—"}</span>
          <span>{formatDateNice(item.initial_warranty_to) || "—"}</span>
        </div>
        <div className="mt-1.5 h-1.5 rounded-full bg-muted overflow-hidden">
          <div
            className={`h-full ${isValid ? "bg-emerald-500" : "bg-red-400"}`}
            style={{ width: `${pct}%` }}
            aria-label={`warranty progress ${pct.toFixed(0)}%`}
          />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {item.present_condition && (
          <Badge className="bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/10 border-0 text-[11px]">{item.present_condition}</Badge>
        )}
        {item.verified_sa && (
          <Badge className="bg-brand-orange/10 text-brand-orange hover:bg-brand-orange/10 border-0 text-[11px]">Secretary {item.verified_sa}</Badge>
        )}
      </div>
    </Card>
  );
}

function TechnicalSpecsCard({ item }) {
  const chunks = splitSpecs(item.specifications);
  const singleBlock = !chunks.length && item.specifications;
  return (
    <Card className="p-6" data-testid="record-specs-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-lg font-bold">Technical specifications</p>
          <div className="h-1 w-10 bg-brand-orange mt-1 rounded" aria-hidden />
        </div>
        <Badge className="bg-brand-blue/10 text-brand-blue hover:bg-brand-blue/10 border-0 text-[11px]">
          Hardware profile
        </Badge>
      </div>
      {chunks.length ? (
        <div className="flex flex-wrap gap-2.5" data-testid="record-specs-list">
          {chunks.map((c, i) => (
            <div
              key={i}
              className="border border-border rounded-lg p-3 flex items-start gap-2 flex-grow basis-[240px] max-w-full"
            >
              <span className="w-2 h-2 rounded-full bg-brand-blue shrink-0 mt-1.5" aria-hidden="true" />
              <span className="text-sm font-semibold break-words whitespace-pre-wrap leading-snug">{c}</span>
            </div>
          ))}
          {item.cd_docs && (
            <div className="border border-border rounded-lg p-3 flex-grow basis-[240px] max-w-full">
              <div className="text-[10px] tracking-[0.12em] font-semibold text-muted-foreground uppercase">Documents</div>
              <div className="text-sm font-semibold mt-1 break-words">{item.cd_docs}</div>
            </div>
          )}
        </div>
      ) : singleBlock ? (
        <div className="border border-border rounded-lg p-4 bg-muted/20" data-testid="record-specs-block">
          <p className="text-sm font-medium leading-relaxed break-words whitespace-pre-wrap">{item.specifications}</p>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No specifications recorded yet.</p>
      )}
    </Card>
  );
}

function AmcParticularsCard({ item }) {
  const amc = reminderStatus(item.amc_to);
  const hasAmc = item.amc_from || item.amc_to;
  const amcBadgeClass = amc.status === "expired"
    ? "bg-red-500/10 text-red-700 border-red-200"
    : amc.status === "expiring_soon"
      ? "bg-amber-500/10 text-amber-700 border-amber-200"
      : hasAmc
        ? "bg-emerald-500/10 text-emerald-700 border-emerald-200"
        : "bg-slate-500/10 text-slate-600 border-slate-200";
  const amcBadgeLabel = amc.status === "expired" ? "Expired" : amc.status === "expiring_soon" ? "Ending soon" : hasAmc ? "Service" : "None";
  return (
    <Card className="p-6" data-testid="record-amc-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-lg font-bold">AMC particulars</p>
          <div className="h-1 w-10 bg-brand-blue mt-1 rounded" aria-hidden />
        </div>
        <span className={`inline-flex items-center text-[11px] font-semibold rounded-full px-2.5 py-1 border ${amcBadgeClass}`}>
          {amcBadgeLabel}
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
        <InfoTile label="AMC period" value={(item.amc_from || item.amc_to) ? `${formatDateNice(item.amc_from) || "…"} → ${formatDateNice(item.amc_to) || "…"}` : ""} />
        <InfoTile label="Service provider" value={item.service_provider} />
        <InfoTile label="AMC No. / Amount" value={item.amc_no_amount} />
        <InfoTile label="Secretary initials" value={item.initials_of_secretary} />
        <InfoTile label="Verified (SA)" value={item.verified_sa} />
        <InfoTile label="Record status" value={amcRecordStatus(item)} />
      </div>
    </Card>
  );
}

function MaintenanceHistoryCard({ item, entries = [] }) {
  const rows = entries;
  return (
    <Card className="p-6" data-testid="record-history-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-lg font-bold">Complaint &amp; maintenance history</p>
          <div className="h-1 w-10 bg-brand-orange mt-1 rounded" aria-hidden />
        </div>
        <Badge className="bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/10 border-0 text-[11px]">
          {rows.length} {rows.length === 1 ? "entry" : "entries"}
        </Badge>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="text-left text-[10px] tracking-[0.12em] font-semibold text-muted-foreground uppercase border-b border-border">
              <th className="py-2 pr-2 w-16">Comp. ID</th>
              <th className="py-2 px-2 w-28">Reported</th>
              <th className="py-2 px-2">Complaint remarks</th>
              <th className="py-2 px-2 w-28">Service date</th>
              <th className="py-2 px-2 w-16">Solved</th>
              <th className="py-2 px-2">Service remarks</th>
              <th className="py-2 pl-2">Technician &amp; contact</th>
            </tr>
          </thead>
          <tbody>
            {!rows.length ? (
              <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">No complaints logged for this asset yet.</td></tr>
            ) : rows.map((e, i) => (
              <tr key={e.id || i} className="border-b border-border/50 align-top">
                <td className="py-2.5 pr-2 font-mono text-xs font-bold">{e.comp_id || i + 1}</td>
                <td className="py-2.5 px-2">{formatDateNice(e.reporting_date) || "—"}</td>
                <td className="py-2.5 px-2">{e.complaint_remarks || "—"}</td>
                <td className="py-2.5 px-2">{formatDateNice(e.service_date) || "—"}</td>
                <td className="py-2.5 px-2">{e.solved === "Yes" ? <span className="text-emerald-700 font-semibold">Yes</span> : e.solved === "No" ? <span className="text-red-600 font-semibold">No</span> : "—"}</td>
                <td className="py-2.5 px-2">{e.service_remarks || "—"}</td>
                <td className="py-2.5 pl-2">{e.technician_contact || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}


/* ---------- QR Label dialog (printable asset sticker) ---------- */
function QrLabelDialog({ open, item, onClose }) {
  if (!item) return null;
  // The QR payload is the public asset URL (or the asset_id if URL is unknown).
  const origin = typeof window !== "undefined" ? window.location.origin : "https://kelta.co.in";
  const payload = item.asset_id
    ? `${origin}/hardware/${item.asset_id}`
    : `${origin}/hardware/${item.id}`;

  const printLabel = () => {
    const svg = document.querySelector("[data-qr-svg]");
    if (!svg) return;
    // One A4 portrait sheet with a 3×3 grid (9 labels max per page)
    const cell = `
    <div class="label">
      <div class="qr">${svg.outerHTML}</div>
      <div class="meta">
        <div class="brand">KELTA · Asset</div>
        <div class="aid">${item.asset_id || item.id || ""}</div>
        <div class="item">${(item.item_name || "").replace(/</g, "&lt;")}</div>
        <div class="sub">${(item.brand || "").replace(/</g, "&lt;")} ${(item.subtype ? "· " + item.subtype : "").replace(/</g, "&lt;")}</div>
        <div class="sub">${(item.institution_name || item.local_body_name || "").replace(/</g, "&lt;")}</div>
      </div>
    </div>`;
    const html = `
<!doctype html><html><head><meta charset="utf-8"><title>Asset Labels ${item.asset_id || ""}</title>
<style>
  @page { size: A4 portrait; margin: 8mm; }
  body { font-family: Arial, sans-serif; margin: 0; color: #1f2937; }
  .sheet { display: grid; grid-template-columns: repeat(3, 1fr); grid-auto-rows: 30mm; gap: 4mm; }
  .label { display: flex; gap: 2.5mm; align-items: center; border: 1px dashed #9ca3af; padding: 2.5mm; border-radius: 2mm; page-break-inside: avoid; overflow: hidden; }
  .qr svg { width: 21mm; height: 21mm; display: block; }
  .meta { font-size: 7pt; line-height: 1.3; min-width: 0; }
  .brand { font-weight: 700; color: #2563eb; font-size: 7pt; letter-spacing: .06em; }
  .aid { font-family: monospace; font-weight: 700; font-size: 8.5pt; margin-top: 1mm; word-break: break-all; }
  .item { margin-top: 0.5mm; font-weight: 600; }
  .sub { color: #6b7280; }
</style></head>
<body>
  <div class="sheet">${cell.repeat(9)}</div>
  <script>window.onload = () => setTimeout(() => window.print(), 300);</script>
</body></html>`;
    const w = window.open("", "_blank", "width=800,height=1000");
    if (w) {
      w.document.open();
      w.document.write(html);
      w.document.close();
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="hardware-qr-dialog">
        <DialogHeader>
          <DialogTitle>Asset QR Label</DialogTitle>
          <DialogDescription>Prints a 3×3 sheet (9 labels) on one A4 page. Cut along the dashed lines and paste on the physical equipment.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3 py-2">
          <div className="p-4 bg-white rounded-lg border border-border">
            <QRCodeSVG value={payload} size={196} level="M" data-qr-svg />
          </div>
          <div className="text-center">
            <p className="text-[10px] tracking-[0.14em] font-semibold text-brand-blue uppercase">KELTA · Asset</p>
            <p className="font-mono font-bold text-lg mt-0.5" data-testid="qr-asset-id">{item.asset_id || item.id}</p>
            <p className="font-semibold text-sm mt-0.5">{item.item_name}</p>
            <p className="text-xs text-muted-foreground">
              {[item.brand, item.subtype].filter(Boolean).join(" · ")}
            </p>
            <p className="text-xs text-muted-foreground">{item.institution_name || item.local_body_name}</p>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={printLabel} className="bg-brand-blue hover:bg-brand-blue/90 text-white" data-testid="qr-print-btn">
            <Printer className="w-4 h-4 mr-1.5" /> Print label
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ---------- Audit trail card ---------- */
const AUDIT_LABELS = {
  create: { label: "Created", cls: "bg-emerald-500/10 text-emerald-700 border-emerald-200" },
  update: { label: "Edited", cls: "bg-brand-blue/10 text-brand-blue border-brand-blue/30" },
  delete: { label: "Deleted", cls: "bg-red-500/10 text-red-700 border-red-200" },
  maintenance_add: { label: "Complaint logged", cls: "bg-amber-500/10 text-amber-700 border-amber-200" },
  attachment_add: { label: "File attached", cls: "bg-slate-500/10 text-slate-700 border-slate-200" },
};

function AuditTrailCard({ item }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!item?.id) return;
    setLoading(true);
    api.get(`/hardware/${item.id}/audit`)
      .then(r => setEntries(r.data || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [item?.id, item?.updated_at]);

  return (
    <Card className="p-6" data-testid="record-audit-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-lg font-bold">Activity trail</p>
          <div className="h-1 w-10 bg-brand-blue mt-1 rounded" aria-hidden />
        </div>
        <Badge className="bg-brand-blue/10 text-brand-blue hover:bg-brand-blue/10 border-0 text-[11px]">
          {entries.length} event{entries.length === 1 ? "" : "s"}
        </Badge>
      </div>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">No activity yet.</p>
      ) : (
        <ol className="relative border-l-2 border-border pl-4 space-y-3">
          {entries.map((e) => {
            const meta = AUDIT_LABELS[e.action] || { label: e.action, cls: "bg-slate-500/10 text-slate-700 border-slate-200" };
            const when = e.at ? new Date(e.at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
            const summary = e.details?.filename
              || e.details?.complaint
              || (e.details?.fields ? `${e.details.fields.length} field${e.details.fields.length === 1 ? "" : "s"} updated` : "")
              || e.details?.item_name
              || "";
            return (
              <li key={e.id} className="relative" data-testid={`audit-entry-${e.id}`}>
                <span className="absolute -left-[21px] top-2 w-2 h-2 rounded-full bg-brand-blue" />
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-block text-[10px] font-semibold rounded-full px-2 py-0.5 border ${meta.cls}`}>{meta.label}</span>
                  <span className="text-xs text-muted-foreground">{when}</span>
                </div>
                <div className="text-sm mt-0.5">
                  <span className="font-medium">{e.user_name || e.user_email || "System"}</span>
                  {summary ? <span className="text-muted-foreground"> — {summary}</span> : null}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}




/* ---------- Multi-asset QR sheet dialog — up to 9 different labels per A4 page ---------- */
function QrSheetDialog({ open, items, onClose }) {
  const [selected, setSelected] = useState(() => new Set());
  const origin = typeof window !== "undefined" ? window.location.origin : "https://kelta.co.in";

  useEffect(() => {
    if (open) setSelected(new Set((items || []).slice(0, 9).map(i => i.id)));
  }, [open, items]);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const chosen = (items || []).filter(i => selected.has(i.id));
  const pages = Math.ceil(chosen.length / 9) || 0;

  const printSheet = () => {
    if (!chosen.length) return;
    const esc = s => String(s || "").replace(/</g, "&lt;");
    const labelHtml = (it) => {
      const svg = document.querySelector(`[data-qr-sheet-svg="${it.id}"] svg`);
      if (!svg) return "";
      const payload = `${origin}/hardware/${it.asset_id || it.id}`;
      return `
    <div class="label">
      <div class="qr">${svg.outerHTML}</div>
      <div class="meta">
        <div class="brand">KELTA · Asset</div>
        <div class="aid">${esc(it.asset_id || it.id)}</div>
        <div class="item">${esc(it.item_name)}</div>
        <div class="sub">${esc(it.brand)} ${it.subtype ? "· " + esc(it.subtype) : ""}</div>
        <div class="sub">${esc(it.institution_name || it.local_body_name)}</div>
        <div class="url">${esc(payload)}</div>
      </div>
    </div>`;
    };
    const chunks = [];
    for (let i = 0; i < chosen.length; i += 9) chunks.push(chosen.slice(i, i + 9));
    const sheets = chunks.map((c, ci) =>
      `<div class="sheet"${ci < chunks.length - 1 ? ' style="page-break-after: always;"' : ""}>${c.map(labelHtml).join("")}</div>`
    ).join("");
    const html = `
<!doctype html><html><head><meta charset="utf-8"><title>Asset QR Labels (${chosen.length})</title>
<style>
  @page { size: A4 portrait; margin: 8mm; }
  body { font-family: Arial, sans-serif; margin: 0; color: #1f2937; }
  .sheet { display: grid; grid-template-columns: repeat(3, 1fr); grid-auto-rows: 30mm; gap: 4mm; }
  .label { display: flex; gap: 2.5mm; align-items: center; border: 1px dashed #9ca3af; padding: 2.5mm; border-radius: 2mm; page-break-inside: avoid; overflow: hidden; }
  .qr svg { width: 21mm; height: 21mm; display: block; }
  .meta { font-size: 7pt; line-height: 1.3; min-width: 0; }
  .brand { font-weight: 700; color: #2563eb; font-size: 7pt; letter-spacing: .06em; }
  .aid { font-family: monospace; font-weight: 700; font-size: 8.5pt; margin-top: 1mm; word-break: break-all; }
  .item { margin-top: 0.5mm; font-weight: 600; }
  .sub { color: #6b7280; }
  .url { color: #9ca3af; font-size: 5.4pt; margin-top: 0.8mm; word-break: break-all; }
</style></head>
<body>
  ${sheets}
  <script>window.onload = () => setTimeout(() => window.print(), 400);</script>
</body></html>`;
    const w = window.open("", "_blank", "width=900,height=1200");
    if (w) {
      w.document.open();
      w.document.write(html);
      w.document.close();
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col" data-testid="qr-sheet-dialog">
        <DialogHeader>
          <DialogTitle>Print QR Label Sheet</DialogTitle>
          <DialogDescription>
            Select assets — prints up to 9 different QR labels per A4 page.
            {chosen.length > 0 && ` ${chosen.length} selected · ${pages} page${pages === 1 ? "" : "s"}.`}
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 mb-2">
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setSelected(new Set((items || []).map(i => i.id)))} data-testid="qr-sheet-select-all">Select all</Button>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setSelected(new Set())} data-testid="qr-sheet-select-none">Clear</Button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto space-y-1.5 pr-1" data-testid="qr-sheet-list">
          {(items || []).map((it, i) => (
            <label key={it.id}
              className={`flex items-center gap-3 border rounded-lg p-2.5 cursor-pointer transition-colors ${selected.has(it.id) ? "border-brand-blue bg-brand-blue/5" : "border-border hover:bg-muted/40"}`}
              data-testid={`qr-sheet-item-${i}`}>
              <input type="checkbox" checked={selected.has(it.id)} onChange={() => toggle(it.id)} />
              <div className="min-w-0">
                <span className="font-mono text-xs font-bold text-brand-blue">{it.asset_id || `#${it.sl_no}`}</span>
                <span className="text-sm ml-2">{it.item_name || "Untitled"}</span>
                <span className="text-xs text-muted-foreground ml-2">{[it.subtype || it.type_of_instrument, it.brand].filter(Boolean).join(" · ")}</span>
              </div>
            </label>
          ))}
        </div>
        {/* Hidden QR SVGs for the selected assets (harvested into the print sheet) */}
        <div className="hidden" aria-hidden>
          {chosen.map(it => (
            <div key={it.id} data-qr-sheet-svg={it.id}>
              <QRCodeSVG value={`${origin}/hardware/${it.asset_id || it.id}`} size={128} level="M" includeMargin={false} />
            </div>
          ))}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={printSheet} disabled={!chosen.length} className="bg-brand-blue hover:bg-brand-blue/90 text-white" data-testid="qr-sheet-print">
            <Printer className="w-4 h-4 mr-1.5" /> Print sheet ({chosen.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
